import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  KeyRound, Plus, X, Loader2, Lock, AlertTriangle, Ban, Save, CircleHelp,
} from 'lucide-react';
import {
  fetchAccesos, registrarAcceso, revocarAcceso, revocarAccesoDeducido, fetchRecursosCredencial,
} from '../../../peticiones_API/PeticionesFichaAccesos';
import { fmtFecha } from './fecha';

/**
 * Fase 13 · §5.4 — la pestaña de Credenciales.
 *
 * ── AQUÍ NO HAY SECRETOS, Y ES UNA DECISIÓN ───────────────────────────────────────────────
 *
 * El `valor` de una credencial es un **identificador**: un correo, un nombre de usuario, una
 * extensión. Por eso tampoco se cifra, y por eso esta pestaña no tiene puerta de segundo factor.
 * **La vecindad en una pantalla no es una clasificación**: estar al lado de RRHH no convierte
 * esto en confidencial.
 *
 * Lo que sí tiene puerta es **a dónde lleva el candado**: la pestaña Soporte, que ya la exige.
 *
 * ── DEDUCIDA FRENTE A REGISTRADA ──────────────────────────────────────────────────────────
 *
 * Una **registrada** la creó alguien y consta cuándo. Una **deducida** es una inferencia a partir
 * de una columna de la ficha, y por eso se enseña **cuál**: sin ver la señal, quien mira no puede
 * juzgar si la inferencia es buena — y una inferencia que no se puede juzgar acaba creyéndose o
 * ignorándose en bloque.
 *
 * La **señal débil** es la que viene de una columna de contraseña en vez de una de identidad.
 * Va marcada aparte porque no equivale: `usuario_nas` está en 233 personas y `password_nas` en 2.
 *
 * ── Y EL IDENTIFICADOR QUE NO PUEDE VIAJAR ────────────────────────────────────────────────
 *
 * El del NAS vive tras la puerta de Soporte. El servidor **no lo manda** sin ventana, y aquí se
 * pinta la fila **sin su valor y con el candado**. La credencial se ve; su identificador, no.
 *
 * Ocultar la fila entera convertiría «no lo puedes ver» en «no lo tiene», que es exactamente la
 * distinción que el material de las colaboradoras existe para no perder.
 */

const claseInput = 'w-full rounded-v25-md border border-v25-line bg-white px-3 py-2 text-[13px] text-v25-ink-900';

/** El estado de una credencial, con su color. `null` es una deducida: no tiene estado todavía. */
const TONO_ESTADO = {
  pendiente: 'bg-v25-warn-bg text-v25-warn-fg',
  creada: 'bg-v25-ok-bg text-v25-ok-fg',
  revocada: 'bg-v25-expired-bg text-v25-expired-fg',
};

function Etiqueta({ tono, children, titulo }) {
  return (
    <span title={titulo}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${tono}`}>
      {children}
    </span>
  );
}

export function PestanaCredenciales({
  personaId, tipo = null, puedeEditar = false, clave = 'credenciales',
  alCambiar = null, registrarGuardado = null, ventana = null, alContar = null,
}) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [recursos, setRecursos] = useState([]);
  const [abriendoForm, setAbriendoForm] = useState(false);
  const [recursoElegido, setRecursoElegido] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const d = await fetchAccesos(personaId, tipo, ventana);
      setDatos(d);
      if (alContar) alContar(d.resumen);
    } catch (e) {
      toast.error('No se pudieron cargar los accesos.');
      setDatos(null);
    } finally {
      setCargando(false);
    }
  }, [personaId, tipo, ventana, alContar]);

  useEffect(() => { if (personaId) cargar(); }, [personaId, cargar]);

  useEffect(() => {
    if (!puedeEditar) return;
    fetchRecursosCredencial().then(setRecursos).catch(() => setRecursos([]));
  }, [puedeEditar]);

  /* ── cambios sin guardar ──────────────────────────────────────────────────────────────── */
  // Como en Formación: es un formulario de ALTA, así que hay cambios cuando hay algo elegido y
  // todavía no se ha pulsado. Revocar no cuenta: se aplica al momento.
  const hayCambios = abriendoForm && !!recursoElegido;

  useEffect(() => { if (alCambiar) alCambiar(clave, hayCambios); }, [alCambiar, clave, hayCambios]);
  useEffect(() => () => { if (alCambiar) alCambiar(clave, false); }, [alCambiar, clave]);
  useEffect(() => {
    if (!hayCambios) return undefined;
    const alSalir = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
    window.addEventListener('beforeunload', alSalir);
    return () => window.removeEventListener('beforeunload', alSalir);
  }, [hayCambios]);

  /**
   * Lo llama el «Guardar» de la ficha, como en Formación y RRHH.
   *
   * Devuelve `true` si guardó o si no había nada que guardar, y `false` si falló — de eso depende
   * que «Guardar y Salir» salga, y salir tras un error se llevaría lo escrito.
   */
  const registrar = async () => {
    if (!abriendoForm) return true;
    if (!recursoElegido) { toast.warn('Elige qué acceso quieres registrar.'); return false; }
    setGuardando(true);
    try {
      try {
        await registrarAcceso(personaId, Number(recursoElegido), tipo);
      } catch (e) {
        /* YA LO TUVO Y ESTÁ REVOCADO: no se registra otro sin decirlo. Registrar es darle el acceso
         * otra vez (un reingreso), y no la forma de deshacer un clic equivocado en una baja: eso se
         * deshace en la baja. Antes del 14/10 esto creaba una segunda fila sin avisar. */
        const c = e?.response?.data;
        if (e?.response?.status !== 409 || c?.error !== 'revocada_antes') throw e;
        const cuando = c.revocada_en ? ` el ${fmtFecha(c.revocada_en)}` : '';
        const quien = c.revocada_por_nombre ? ` por ${c.revocada_por_nombre}` : '';
        if (!window.confirm(`Este acceso ya lo tuvo y se revocó${cuando}${quien}.\n\n`
          + 'Registrar uno nuevo es darle el acceso otra vez. Si fue un clic equivocado en una baja, '
          + 'deshazlo allí en vez de esto.\n\n¿Registrar uno nuevo?')) return false;
        await registrarAcceso(personaId, Number(recursoElegido), tipo, true);
      }
      setRecursoElegido(''); setAbriendoForm(false);
      await cargar();
      toast.success('Acceso registrado.');
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.message || 'No se pudo registrar el acceso.');
      return false;
    } finally {
      setGuardando(false);
    }
  };

  // Envoltorio estable sobre una referencia que se refresca en cada render: registrar la función
  // directamente obligaría a re-registrar con cada tecla, y registrarla una sola vez congelaría la
  // del primer render, que ve el formulario cerrado y no guardaría nunca.
  const registrarRef = useRef(null);
  registrarRef.current = registrar;
  useEffect(() => {
    if (!registrarGuardado) return undefined;
    registrarGuardado(clave, () => registrarRef.current());
    return () => registrarGuardado(clave, null);
  }, [registrarGuardado, clave]);

  /**
   * Revocar. **Fuera del botón Guardar, y a propósito.**
   *
   * No todo el mundo que pierde un acceso se está yendo: alguien cambia de puesto y deja de
   * necesitar el CRM. Es una acción inmediata con su confirmación, como borrar una titulación —
   * atarla a «Guardar» convertiría un botón en dos cosas distintas.
   */
  const revocar = async (c) => {
    const quees = c.origen === 'deducida' ? 'deducido' : 'registrado';
    if (!window.confirm(`Revocar el acceso ${quees} «${c.nombre}»?\n\nQuedará registrado quién y cuándo.`)) return;
    try {
      if (c.origen === 'deducida') await revocarAccesoDeducido(personaId, c.recurso_id, tipo);
      else await revocarAcceso(personaId, c.id, tipo);
      await cargar();
      toast.success('Acceso revocado.');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'No se pudo revocar.');
    }
  };

  const irASoporte = () => {
    const p = new URLSearchParams(window.location.search);
    p.set('tab', 'soporte');
    window.location.assign(`${window.location.pathname}?${p.toString()}`);
  };

  const filas = datos?.credenciales || [];
  const noDeducibles = datos?.credenciales_no_deducibles || [];

  return (
    <section className="rounded-v25-lg border border-v25-line bg-white p-4">
      <header className="mb-3 flex items-center justify-between border-b border-v25-line pb-2">
        <div className="flex items-center gap-2">
          <KeyRound size={17} className="text-v25-brand-600" />
          <h4 className="text-[14px] font-semibold text-v25-ink-900">
            Accesos de esta persona {filas.length ? `(${filas.length})` : ''}
          </h4>
        </div>
        {puedeEditar && !abriendoForm && (
          <button type="button" onClick={() => setAbriendoForm(true)}
            className="inline-flex items-center gap-1 rounded-v25-md border border-v25-line bg-white px-3 py-1.5 text-[13px] font-semibold text-v25-ink-700 shadow-v25-btn">
            <Plus size={14} /> Registrar un acceso
          </button>
        )}
      </header>

      <p className="mb-3 text-[12px] leading-relaxed text-v25-ink-500">
        Los <strong>registrados</strong> los creó alguien y consta cuándo. Los <strong>deducidos</strong>
        {' '}son una inferencia a partir de las columnas de su ficha, y se indica de cuál.
      </p>

      {abriendoForm && (
        <div className="mb-4 rounded-v25-md border border-v25-line bg-v25-surface-sunken p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[13px] font-semibold text-v25-ink-900">Registrar un acceso</span>
            <button type="button" onClick={() => { setAbriendoForm(false); setRecursoElegido(''); }}
              aria-label="Cancelar" className="text-v25-ink-500"><X size={16} /></button>
          </div>
          <select className={claseInput} aria-label="Acceso" value={recursoElegido}
            onChange={(e) => setRecursoElegido(e.target.value)}>
            <option value="">Elige el acceso…</option>
            {recursos.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
          </select>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-[12px] text-v25-ink-500">
              <Save size={14} />
              Se registra con <strong className="font-semibold text-v25-ink-700">Guardar</strong> al
              final de la ficha. Nace como <strong>pendiente</strong>: registrarlo dice que hace
              falta, no que ya exista.
            </span>
            {guardando && (
              <span className="ml-auto inline-flex items-center gap-1.5 text-[12px] font-medium text-v25-ink-700">
                <Loader2 size={14} className="animate-spin" /> Guardando…
              </span>
            )}
            {!guardando && hayCambios && (
              <span className="ml-auto inline-flex items-center gap-1.5 text-[12px] font-medium text-v25-ink-500">
                <AlertTriangle size={14} /> Sin guardar
              </span>
            )}
          </div>
        </div>
      )}

      {cargando && (
        <div className="flex items-center gap-2 p-6 text-[13px] text-v25-ink-500">
          <Loader2 size={16} className="animate-spin" /> Cargando…
        </div>
      )}

      {!cargando && !filas.length && (
        <p className="rounded-v25-md border border-dashed border-v25-line px-3 py-3 text-[12px] text-v25-ink-500">
          Sin accesos registrados ni deducidos.
        </p>
      )}

      {!cargando && filas.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-v25-line text-left text-[12px] font-semibold text-v25-ink-700">
                <th className="py-2 pr-3">Acceso</th>
                <th className="py-2 pr-3">Identificador</th>
                <th className="py-2 pr-3">Estado</th>
                <th className="py-2 pr-3">Origen</th>
                <th className="py-2 pr-3">Contraseña</th>
                {puedeEditar && <th className="py-2" aria-label="Acciones" />}
              </tr>
            </thead>
            <tbody>
              {filas.map((c) => (
                <tr key={`${c.recurso_id}-${c.id || 'ded'}`} className="border-b border-v25-line last:border-0">
                  <td className="py-2 pr-3 text-v25-ink-900">{c.nombre}</td>

                  <td className="py-2 pr-3 text-v25-ink-700">
                    {c.valor_oculto ? (
                      // La fila SE VE; su identificador, no. «No lo puedes ver» no es «no lo tiene».
                      <span className="inline-flex items-center gap-1.5 text-v25-ink-500"
                        title={c.valor_oculto_motivo}>
                        <Lock size={13} /> <span className="italic">oculto</span>
                      </span>
                    ) : (c.valor || <span className="text-v25-ink-400">—</span>)}
                  </td>

                  <td className="py-2 pr-3">
                    {c.estado
                      ? <Etiqueta tono={TONO_ESTADO[c.estado] || 'bg-v25-surface-sunken text-v25-ink-700'}>{c.estado}</Etiqueta>
                      : <span className="text-v25-ink-400">—</span>}
                  </td>

                  <td className="py-2 pr-3">
                    {c.origen === 'deducida' ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="inline-flex items-center gap-1">
                          <Etiqueta tono="bg-v25-surface-sunken text-v25-ink-700">deducida</Etiqueta>
                          {c.senal_debil && (
                            // La señal débil, marcada: viene de una contraseña y no de una identidad.
                            <Etiqueta tono="bg-v25-warn-bg text-v25-warn-fg"
                              titulo="Se deduce de una columna de contraseña, no de identidad: es una señal más floja.">
                              <AlertTriangle size={11} /> señal débil
                            </Etiqueta>
                          )}
                        </span>
                        {/* LA COLUMNA QUE LA DELATA, debajo. Sin esto la inferencia no se puede juzgar. */}
                        <code className="text-[11px] text-v25-ink-500">{(c.senal || []).join(', ')}</code>
                      </div>
                    ) : <Etiqueta tono="bg-v25-brand-50 text-v25-brand-800">registrada</Etiqueta>}
                  </td>

                  <td className="py-2 pr-3">
                    {c.campo_password ? (
                      <button type="button" onClick={irASoporte}
                        title="Las contraseñas se ven en Soporte, con verificación en dos pasos."
                        className="inline-flex items-center gap-1 text-v25-brand-600 hover:underline">
                        <Lock size={13} /> Ver en Soporte
                      </button>
                    ) : <span className="text-v25-ink-400">—</span>}
                  </td>

                  {puedeEditar && (
                    <td className="py-2 text-right">
                      {c.estado === 'revocada' ? (
                        <span className="text-[12px] text-v25-ink-400">revocado</span>
                      ) : (
                        <button type="button" onClick={() => revocar(c)}
                          aria-label={`Revocar ${c.nombre}`}
                          className="inline-flex items-center gap-1 text-[12px] text-v25-ink-500 hover:text-v25-expired-fg">
                          <Ban size={14} /> Revocar
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* LOS QUE NO SE PUEDEN COMPROBAR SOLOS. No generan alarma —saltaría siempre— pero la
          pantalla tiene que poder decir que existen: «no tiene» y «no lo sabemos» no son lo mismo. */}
      {!cargando && noDeducibles.length > 0 && (
        <div className="mt-4 rounded-v25-md border border-dashed border-v25-line bg-v25-surface-sunken px-3 py-3">
          <p className="flex items-start gap-2 text-[12px] leading-relaxed text-v25-ink-700">
            <CircleHelp size={15} className="mt-0.5 shrink-0 text-v25-ink-500" />
            <span>
              <strong>{noDeducibles.length === 1 ? 'Un acceso del catálogo no se puede comprobar solo' : `${noDeducibles.length} accesos del catálogo no se pueden comprobar solos`}</strong>
              {' — '}
              {noDeducibles.map((r) => `«${r.nombre}»`).join(', ')}
              {noDeducibles.length === 1 ? ' no está' : ' no están'} en ninguna columna de la ficha.
              Si esta persona {noDeducibles.length === 1 ? 'lo tiene' : 'los tiene'}, hay que
              registrar{noDeducibles.length === 1 ? 'lo' : 'los'} a mano.
            </span>
          </p>
        </div>
      )}

      <p className="mt-3 text-[12px] leading-relaxed text-v25-ink-500">
        Los identificadores no son secretos y se ven con el permiso normal de la ficha. Las
        contraseñas están en Soporte, con verificación en dos pasos.
      </p>
    </section>
  );
}

export default PestanaCredenciales;
